import React from 'react';
import {
  IconButton,
  Dropdown,
  Form,
  Grid,
  GridRow,
  GridColumn,
  Layout,
  Well,
  CondensedLarge,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateLanguage,
  CandidateRedux,
  Language,
} from '@axiom/validation';
import { LanguageConst } from '@axiom/const';

import type { TalentProfileEditFormType } from '../../schema/talent-profile-edit-form-schema';
import { TalentProfileEditLanguageFormSchema } from '../../schema/talent-profile-edit-language-form-schema';

const languageSkillItems = Object.keys(LanguageConst.LANGUAGE_SKILLS).map(
  key => ({
    label:
      LanguageConst.LANGUAGE_SKILLS[
        key as keyof typeof LanguageConst.LANGUAGE_SKILLS
      ],
    value: key,
  })
);
const languageCEFRItems = Object.keys(LanguageConst.LANGUAGE_CEFR_LEVELS).map(
  key => ({
    label:
      LanguageConst.LANGUAGE_CEFR_LEVELS[
        key as keyof typeof LanguageConst.LANGUAGE_CEFR_LEVELS
      ],
    value: key,
  })
);
const languageProficiencyItems = Object.keys(
  LanguageConst.LANGUAGE_PROFICIENCIES
).map(key => ({
  label:
    LanguageConst.LANGUAGE_PROFICIENCIES[
      key as keyof typeof LanguageConst.LANGUAGE_PROFICIENCIES
    ],
  value: key,
}));

export interface TalentProfileEditFormLanguages {
  name?: string;
  languages: Language[];
  candidate: CandidateRedux | Candidate;
  fields?: TalentProfileEditFormType['languages'];
  addParentFormLanguageValue: (
    candidateLanguage: Partial<CandidateLanguage>
  ) => Promise<void>;
  removeParentFormLanguageValue: (index: number) => Promise<void>;
}
export const TalentProfileEditFormLanguages = ({
  name = 'LANGUAGE_FORM',
  languages,
  fields,
  addParentFormLanguageValue,
  removeParentFormLanguageValue,
}: TalentProfileEditFormLanguages) => {
  return (
    <Form
      name={name}
      schema={TalentProfileEditLanguageFormSchema}
      onSubmit={async (changedData, action, formData) => {
        const { languageId, proficiency, skill, cefr } = formData;
        const newLanguage = {
          languageId,
          languageProficiency: proficiency,
          languageSkill: skill,
          languageCEFR: cefr,
          ...changedData,
        };

        await addParentFormLanguageValue(newLanguage as CandidateLanguage);
        await action.setValues({
          languageId: null,
          proficiency: null,
          skill: null,
          cefr: null,
        });
      }}
      initialValues={{
        languageId: null,
        proficiency: null,
        skill: null,
        cefr: null,
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Grid>
            <GridRow>
              <GridColumn width={3}>
                <Dropdown
                  name="languageId"
                  options={languages.map(l => ({
                    label: l.name,
                    value: l.id,
                  }))}
                  label="Language"
                  displayKey="label"
                  valueKey="value"
                />
              </GridColumn>
              <GridColumn width={3}>
                <Dropdown
                  name="proficiency"
                  options={languageProficiencyItems}
                  label="Proficiency"
                  displayKey="label"
                  valueKey="value"
                  disabled={!values.languageId}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Dropdown
                  name="skill"
                  options={languageSkillItems}
                  label="Language Skill"
                  displayKey="label"
                  valueKey="value"
                  disabled={!values.proficiency}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Layout position="space-between bottom">
                  <Dropdown
                    name="cefr"
                    options={languageCEFRItems}
                    onChange={async () => {
                      await fireSubmit();
                    }}
                    label="CEFR Level"
                    displayKey="label"
                    valueKey="value"
                    disabled={!values.skill}
                  />
                  <IconButton
                    variation="minimal"
                    pattern="secondary"
                    icon="plus"
                    className="plus-icon"
                    disabled={!values.proficiency}
                    onClick={async () => {
                      await fireSubmit();
                    }}
                    name="ADD_LANGUAGE_BUTTON"
                  />
                </Layout>
              </GridColumn>
            </GridRow>

            {fields && fields.length > 0 ? (
              <GridRow gutterTop="24px">
                <GridColumn>
                  <Well>
                    {fields.map((field, i) => {
                      const languageName = languages.find(
                        l => l.id === field.languageId
                      )?.name;
                      return (
                        <Layout
                          name="LANGUAGE_ITEM"
                          position="space-between middle"
                          key={JSON.stringify({
                            ...field,
                            i,
                          })}
                        >
                          <CondensedLarge>
                            {[
                              languageName,
                              field.languageProficiency
                                ? LanguageConst.LANGUAGE_PROFICIENCIES[
                                    field.languageProficiency
                                  ]
                                : null,
                              field.languageSkill
                                ? LanguageConst.LANGUAGE_SKILLS[
                                    field.languageSkill
                                  ]
                                : null,
                              field.languageCEFR,
                            ]
                              .filter(Boolean)
                              .join('; ')}
                          </CondensedLarge>
                          <IconButton
                            variation="minimal"
                            pattern="secondary"
                            icon="trash"
                            name="DELETE_LANGUAGE_BUTTON"
                            onClick={async () => {
                              await removeParentFormLanguageValue(i);
                            }}
                          />
                        </Layout>
                      );
                    })}
                  </Well>
                </GridColumn>
              </GridRow>
            ) : null}
          </Grid>
        );
      }}
    </Form>
  );
};
